import {decodeJwt} from './jwt.ts';
import CustomClaimTypes from './CustomClaimTypes.ts';

/**
 * Detect the "stale chunk" failure: a tab opened before a deploy tries to lazy-load a
 * code-split module whose hashed filename no longer exists on the server (the new build
 * replaced it). It surfaces as a dynamic-import failure with browser-specific wording, so
 * we match all of them rather than the exact Chrome string.
 */
export function isChunkLoadError(error: Error | null): boolean {
    if (!error) {
        return false;
    }
    const haystack = `${error.name} ${error.message}`;
    return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|chunkloaderror/i.test(
        haystack
    );
}

/** Assemble a human-readable diagnostic block for support requests. */
export function buildErrorReport(
    error: Error | null,
    componentStack?: string | null
): string {
    const safe = (fn: () => string): string => {
        try {
            return fn();
        } catch {
            return 'unavailable';
        }
    };

    // The user comes from the access token in sessionStorage rather than React state,
    // because the React tree below the failure may be broken. Only the handle and id go
    // in the report: it gets pasted into messages, so no name or email.
    const claims: Record<string, unknown> = (() => {
        try {
            const token = sessionStorage.getItem('access_token');
            return token ? decodeJwt(token).payload : {};
        } catch {
            return {};
        }
    })();

    const user = safe(() => {
        const id = claims.nameid as string | undefined;
        if (!id) {
            return 'Not signed in / unavailable';
        }
        const handle = claims[CustomClaimTypes.nickname] as string | undefined;
        return `${handle ? `@${handle}` : '(no handle)'}  [id: ${id}]`;
    });

    const page = safe(
        () =>
            `${window.location.pathname}${window.location.search}${window.location.hash}`
    );
    const fullUrl = safe(() => window.location.href);
    const mode = safe(() => import.meta.env.MODE);
    const title = safe(() => document.title);

    const browser = safe(() => navigator.userAgent);
    const language = safe(() => navigator.language);
    const viewport = safe(() => `${window.innerWidth}x${window.innerHeight}`);
    const screenSize = safe(
        () => `${window.screen.width}x${window.screen.height}`
    );
    const online = safe(() => String(navigator.onLine));
    const time = safe(() => new Date().toISOString());

    return [
        '=== Mixiply Error Report ===',
        `Time:       ${time}`,
        `App mode:   ${mode}`,
        `Page:       ${page}`,
        `Full URL:   ${fullUrl}`,
        `Title:      ${title}`,
        `User:       ${user}`,
        '',
        '--- Error ---',
        `${error?.name ?? 'Error'}: ${error?.message ?? '(no message)'}`,
        error?.stack ?? '(no stack trace)',
        '',
        '--- Component stack ---',
        componentStack?.trim() ?? '(no component stack)',
        '',
        '--- Browser ---',
        `User agent: ${browser}`,
        `Language:   ${language}`,
        `Viewport:   ${viewport}   Screen: ${screenSize}`,
        `Online:     ${online}`
    ].join('\n');
}
